import { createFileRoute, Link } from "@tanstack/react-router";
import { SITE_URL } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { fetchCreatorByHandle } from "@/lib/queries";
import { ProductCard } from "@/components/product-card";
import { ShopTheLook } from "@/components/shop-the-look";
import { VerifiedSocials } from "@/components/verified-socials";
import { VerifiedBadge } from "@/components/verified-badge";
import { resolveImage } from "@/lib/image-map";
import { RouteError, RouteNotFound } from "@/components/route-fallbacks";
import { CreatorPageSkeleton } from "@/components/skeleton-card";
import { ImageZoom } from "@/components/image-zoom";
import { LiveCursors } from "@/components/live-cursors";
import { Share2 } from "lucide-react";
import { shareOrCopy } from "@/lib/share";
import { useState } from "react";


export const Route = createFileRoute("/creator/$handle")({
  loader: ({ params }) => fetchCreatorByHandle(params.handle),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  head: ({ params, loaderData }) => {
    const url = `${SITE_URL}/creator/${params.handle}`;
    const c = loaderData?.creator;
    if (!c) {
      return {
        meta: [
          { title: `@${params.handle} — Pluggz` },
          { name: "description", content: `This creator storefront is not available on Pluggz. Browse other vetted creators and their curated picks.` },
          { property: "og:url", content: url },
        ],
        links: [{ rel: "canonical", href: url }],
      };
    }
    const title = `${c.name} (@${c.handle}) — Curated Storefront on Pluggz`;
    const desc = (c.bio?.trim() ||
      `Shop ${c.name}'s curated storefront on Pluggz — handpicked products from a vetted creator with ${c.followers ?? "a growing"} following.`
    ).slice(0, 160);
    const image = c.cover_url ? resolveImage(c.cover_url) : c.avatar_url ? resolveImage(c.avatar_url) : undefined;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: `${c.name} — @${c.handle}` },
        { property: "og:description", content: desc },
        { property: "og:type", content: "profile" },
        { property: "og:url", content: url },
        { property: "profile:username", content: c.handle },
        ...(image ? [{ property: "og:image", content: image }] : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: `${c.name} on Pluggz` },
        { name: "twitter:description", content: desc },
        ...(image ? [{ name: "twitter:image", content: image }] : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfilePage",
            mainEntity: {
              "@type": "Person",
              name: c.name,
              alternateName: `@${c.handle}`,
              description: c.bio || undefined,
              image: image,
              url,
              sameAs: [c.instagram, c.tiktok, c.youtube, c.website].filter(Boolean),
            },
          }),
        },
      ],
    };
  },
  component: CreatorPage,
});

function CreatorPage() {
  const { handle } = Route.useParams();
  const { data, isLoading } = useQuery({
    queryKey: ["creator", handle],
    queryFn: () => fetchCreatorByHandle(handle),
  });
  const [tab, setTab] = useState<"All" | "Collections" | "Looks">("All");


  if (isLoading) return <CreatorPageSkeleton />;
  if (!data) return (
    <div className="mx-auto max-w-xl px-6 py-32 text-center">
      <h1 className="font-serif text-4xl">Creator not found</h1>
      <Link to="/creators" className="mt-6 inline-block text-sm text-accent">Browse creators →</Link>
    </div>
  );

  const { creator, products, collections } = data;

  return (
    <div className="relative">
      <LiveCursors roomId={`creator:${creator.handle}`} />
      <section className="relative h-[42vh] min-h-[320px] max-h-[520px] overflow-hidden bg-muted">
        <ImageZoom
          src={resolveImage(creator.cover_url)}
          alt={`${creator.name} cover`}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-b from-transparent to-background" />
      </section>

      <section className="mx-auto -mt-16 max-w-5xl px-6 text-center">
        <ImageZoom
          src={resolveImage(creator.avatar_url)}
          alt={creator.name}
          className="relative z-10 mx-auto h-32 w-32 rounded-full border-4 border-background object-cover shadow-xl ring-1 ring-accent/30"
        />
        <h1 className="mt-6 font-serif text-4xl md:text-5xl">
          <span className="shimmer">{creator.name}</span>
          {creator.is_verified && (
            <VerifiedBadge className="ml-2 h-5 w-5 align-middle" />
          )}
        </h1>
        <div className="mt-3 flex items-center justify-center gap-3">
          <p className="text-sm uppercase tracking-widest text-muted-foreground">@{creator.handle} · {creator.followers} followers</p>
          <button
            type="button"
            aria-label="Share storefront"
            onClick={() =>
              shareOrCopy({
                title: `${creator.name} on Pluggz`,
                text: creator.bio || `Shop ${creator.name}'s curated picks`,
                url: `${SITE_URL}/creator/${handle}`,
              })
            }
            className="inline-flex items-center gap-2 border border-border px-4 py-2 text-xs uppercase tracking-widest hover:bg-card transition"
          >
            <Share2 className="h-4 w-4" /> Share
          </button>
        </div>
        {creator.bio && (
          <p className="mx-auto mt-6 max-w-xl break-words text-lg italic text-muted-foreground">
            &ldquo;{creator.bio}&rdquo;
          </p>
        )}
        <VerifiedSocials creatorId={creator.id} />
      </section>

      <div className="mx-auto mt-12 max-w-7xl px-6">
        <div className="overflow-x-auto border-b border-border [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          <div className="flex gap-1 whitespace-nowrap">
            {(["All", "Collections", "Looks"] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`flex-shrink-0 px-4 py-3 text-sm tracking-wide transition ${tab === t ? "border-b-2 border-foreground -mb-px" : "text-muted-foreground hover:text-foreground"}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>

      {tab === "All" && (
        <>
      {collections.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 pt-20">
          <p className="editorial-eyebrow">Themed bundles</p>
          <h2 className="mt-2 font-serif text-3xl">Collections</h2>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {collections.map((col) => (
              <div key={col.id} className="group relative aspect-[4/5] overflow-hidden bg-muted card-lift border border-border bg-card rounded-sm">
                <ImageZoom src={resolveImage(col.cover_url)} alt={col.title} loading="lazy" className="ken-burns h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70" />
                <div className="absolute bottom-5 left-5 text-primary-foreground">
                  <p className="text-[10px] uppercase tracking-widest opacity-80">{col.collection_products.length} pieces</p>
                  <p className="mt-1 font-serif text-xl">{col.title}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <ShopTheLook creatorId={creator.id} />

      <section className="mx-auto max-w-7xl px-6 pt-24 pb-24">
        <p className="editorial-eyebrow">The shop</p>
        <h2 className="mt-2 font-serif text-3xl">Everything I'm loving</h2>
        <div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-14 md:grid-cols-3 lg:grid-cols-4">
          {products.map((p) => <ProductCard key={p.id} product={p} byline={`"A studio essential." — ${creator.name.split(" ")[0]}`} />)}
        </div>
      </section>
        </>
      )}

      {tab === "Collections" && (
        <section className="mx-auto max-w-7xl px-6 pt-12 pb-24">
          {collections.length === 0 ? (
            <p className="py-20 text-center text-muted-foreground">No collections yet.</p>
          ) : (
            <div className="grid gap-6 md:grid-cols-3">
              {collections.map((col) => (
                <div key={col.id} className="group relative aspect-[4/5] overflow-hidden bg-muted card-lift border border-border bg-card rounded-sm">
                  <ImageZoom src={resolveImage(col.cover_url)} alt={col.title} loading="lazy" className="ken-burns h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70" />
                  <div className="absolute bottom-5 left-5 text-primary-foreground">
                    <p className="text-[10px] uppercase tracking-widest opacity-80">{col.collection_products.length} pieces</p>
                    <p className="mt-1 font-serif text-xl">{col.title}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {tab === "Looks" && (
        <div className="pb-24">
          <ShopTheLook creatorId={creator.id} />
        </div>
      )}
    </div>
  );
}
